import {
  typedCollection,
  typedDoc,
  getCollection,
  addDoc,
  limit,
  updateDocument,
  orderBy,
  where,
  serverTimestamp,
} from '@/lib/firebase/firestore'
import {
  storage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from '@/lib/firebase/storage'
import type { ProviderInvoiceDocument, ProviderInvoiceLineItem } from '@/types'

const invoicesCol = () => typedCollection<ProviderInvoiceDocument>('providerInvoices')

export interface SubmitInvoiceInput {
  memberId: string
  providerMemberId: string
  assignmentId: string
  engagementId: string
  productionName: string
  lineItems: ProviderInvoiceLineItem[]
  invoiceDate: string
  notes?: string
}

function subtotalOf(items: ProviderInvoiceLineItem[]): number {
  return items.reduce((acc, li) => acc + (Number(li.amount) || 0), 0)
}

export const providerInvoicesService = {
  async listForMember(memberId: string) {
    return getCollection(
      invoicesCol(),
      where('memberId', '==', memberId),
      orderBy('invoiceDate', 'desc'),
      limit(100),
    )
  },

  async createSubmission(input: SubmitInvoiceInput): Promise<string> {
    const now = serverTimestamp()
    const payload = {
      memberId: input.memberId,
      providerMemberId: input.providerMemberId,
      assignmentId: input.assignmentId,
      engagementId: input.engagementId,
      productionName: input.productionName,
      status: 'submitted' as const,
      lineItems: input.lineItems,
      subtotal: subtotalOf(input.lineItems),
      invoiceDate: input.invoiceDate,
      ...(input.notes ? { notes: input.notes } : {}),
      createdBy: input.memberId,
      submittedAt: now,
      createdAt: now,
      updatedAt: now,
    }
    const docRef = await addDoc(invoicesCol(), payload as unknown as ProviderInvoiceDocument)
    return docRef.id
  },

  async updateDraft(
    id: string,
    patch: Partial<Pick<SubmitInvoiceInput, 'lineItems' | 'invoiceDate' | 'notes'>>,
  ): Promise<void> {
    const ref = typedDoc<ProviderInvoiceDocument>('providerInvoices', id)
    const update: Record<string, unknown> = { updatedAt: serverTimestamp() }
    if (patch.lineItems !== undefined) {
      update.lineItems = patch.lineItems
      update.subtotal = subtotalOf(patch.lineItems)
    }
    if (patch.invoiceDate !== undefined) update.invoiceDate = patch.invoiceDate
    if (patch.notes !== undefined) update.notes = patch.notes
    await updateDocument(ref, update as Partial<ProviderInvoiceDocument>)
  },

  async resubmit(
    id: string,
    patch: Partial<Pick<SubmitInvoiceInput, 'lineItems' | 'invoiceDate' | 'notes'>>,
  ): Promise<void> {
    const ref = typedDoc<ProviderInvoiceDocument>('providerInvoices', id)
    const now = serverTimestamp()
    const update: Record<string, unknown> = {
      status: 'submitted',
      submittedAt: now,
      updatedAt: now,
    }
    if (patch.lineItems !== undefined) {
      update.lineItems = patch.lineItems
      update.subtotal = subtotalOf(patch.lineItems)
    }
    if (patch.invoiceDate !== undefined) update.invoiceDate = patch.invoiceDate
    if (patch.notes !== undefined) update.notes = patch.notes
    await updateDocument(ref, update as Partial<ProviderInvoiceDocument>)
  },

  async uploadReceipt(memberId: string, file: File): Promise<string> {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    const path = `providerInvoices/${memberId}/receipts/${Date.now()}-${safeName}`
    const r = storageRef(storage, path)
    const task = uploadBytesResumable(r, file)
    await new Promise<void>((resolve, reject) => {
      task.on('state_changed', undefined, reject, () => resolve())
    })
    return getDownloadURL(r)
  },
}
